/**
 * @file mesh-mixin.ts
 *
 * Owns mesh data state and wires the event listeners for mesh I/O and
 * transfer flows. On connect it loads any pending transfer from IDB and shows
 * the autoload dialog if one is waiting.
 *
 * The overlay click handler is registered directly on as-modal-overlay so that
 * mesh transfer modal interactions (app selection, autoload confirm/cancel,
 * history clear) are routed to handleTxModalClick without bubbling through the
 * host element's own event listeners.
 *
 * All handler logic is delegated to app-mesh.ts.
 *
 * Requires: ModalMixinMembers, ErrorMixinMembers, InitQueueMixinMembers
 * Exposes: meshData, availableApps, openTransferInNewTab, userData,
 *          exporter, initializeAvailableApps, createExporter,
 *          onTransferMeshLoaded
 */
import { checkForStoredMeshData, type StoredMeshData } from '../../../logic/db.ts';
import { getAvailableAppKeys } from '../app-config.ts';
import { inIframe, noAPI } from '../../../data/constants.ts';
import { registerListeners, unregisterListeners } from '../../../logic/utils.ts';
import { type UserData } from '../../../logic/asApi.ts';
import { type FileInfoArr } from '../../../data/data-interfaces.external.ts';
import { ExportBuilder, type ExportBuilderOptions } from '../../../logic/importExport/exportBuilder.ts';
import * as MeshHandlers from '../app-mesh.ts';
import { type Constructor } from './mixin-types-utils.ts';
import type { ModalMixinMembers } from './modal-mixin.ts';
import type { ErrorMixinMembers } from './error-mixin.ts';
import type { InitQueueMixinMembers } from './init-queue-mixin.ts';

/** Surface added by MeshMixin. */
export interface MeshMixinMembers {
  meshData: {
    latestMesh: StoredMeshData | null;
    /** A mesh transferred from another app that should be autoloaded. */
    autoLoadMesh: StoredMeshData | null;
    totalCount: number;
    meshes?: StoredMeshData[];
  };
  /** App ids the current user has access to; populated from the API. */
  availableApps: string[];
  openTransferInNewTab: boolean;
  userData: UserData | null;
  /** Shared export builder. Configure it (clear + addFile / addConfiguratorMesh / fromSchema) then call download() or sendToGrabCAD(). */
  exporter: ExportBuilder;
  /** Create an independent one-shot builder that won't affect the shared exporter. */
  createExporter(opts?: ExportBuilderOptions): ExportBuilder;
  initializeAvailableApps(): Promise<void>;
  /**
   * Rx hook for transferred meshes. When set (assign during app setup, before
   * dispatching configurator-initialized), as-db-mesh-loaded is NOT dispatched;
   * the callback is awaited instead. Resolve -> as-mesh-rx-completed is sent
   * automatically (return { keepStored: true } for shouldDelete: false).
   * Reject -> a persistent error notification offers "Skip transfer" /
   * "Save transfer as file".
   */
  onTransferMeshLoaded: MeshHandlers.TransferMeshLoadedCallback | null;
}

export const MeshMixin = <T extends Constructor<HTMLElement & ModalMixinMembers & ErrorMixinMembers & InitQueueMixinMembers>>(Base: T) =>
  class MeshMixin extends Base {
    meshData: {
      latestMesh: StoredMeshData | null;
      autoLoadMesh: StoredMeshData | null;
      totalCount: number;
      meshes?: StoredMeshData[];
    } = { latestMesh: null, autoLoadMesh: null, totalCount: 0 };
    availableApps: string[] = [];
    openTransferInNewTab: boolean = false;
    userData: UserData | null = null;
    onTransferMeshLoaded: MeshHandlers.TransferMeshLoadedCallback | null = null;

    private _meshSelfListeners: Record<string, EventListener> = {};
    private _meshWindowListeners: Record<string, EventListener> = {};
    private _overlayClickHandler: EventListener = (e) => MeshHandlers.handleTxModalClick(this as any, e as MouseEvent);

    /** Shared export builder. Configure it (clear + addFile) then call download() or sendToGrabCAD(). */
    exporter!: ExportBuilder;

    /** Create an independent one-shot builder. Use when you need an isolated instance that won't affect the shared exporter. */
    createExporter(opts: ExportBuilderOptions = {}): ExportBuilder {
      return new ExportBuilder((this as any).appId as string, opts);
    }

    public async initializeAvailableApps(): Promise<void> {
      try {
        this.availableApps = await getAvailableAppKeys();
      } catch (error) {
        console.error('Failed to initialize available apps:', error);
        this.availableApps = [];
      }
    }

    async connectedCallback() {
      await super.connectedCallback?.();

      if (!inIframe && !noAPI) {
        this.initializeAvailableApps();
      }

      const appId = (this as any).appId as string;

      this.exporter = new ExportBuilder(appId);

      await MeshHandlers.cleanUpStaleMeshes(this as any);
      this.meshData = await checkForStoredMeshData(appId, true, true) as typeof this.meshData;
      if (this.meshData.autoLoadMesh) {
        MeshHandlers.showAutoLoadDialog(this as any, this.meshData.autoLoadMesh);
      }

      this._meshSelfListeners = {
        'show-stored-meshes':   () => MeshHandlers.handleShowStoredMeshes(this as any).catch((e: unknown) => this.logError('show-stored-meshes', e)),
        'mesh-processed':       (e) => MeshHandlers.postMeshReceiveCleanup(this as any, e).catch((err: unknown) => this.logError('mesh-processed', err)),
        'as-mesh-rx-completed': (e) => MeshHandlers.postMeshReceiveCleanup(this as any, e).catch((err: unknown) => this.logError('as-mesh-rx-completed', err)),
        'as-transfer-mesh':     () => MeshHandlers.prepareTransferMesh(this as any).catch((e: unknown) => this.logError('as-transfer-mesh', e)),
        // Unified data-I/O logging. Every import path -> data_import, every export
        // path -> data_export, each tagged with a subcategory ('local' | 'interapp'
        // | 'grabcad'). Producers put subcategory + fileInfo in the event detail and
        // this layer just forwards it; mesh-vs-file is carried in fileInfo[].fileType.
        // Failures stay on the same events via detail.error, but are logged under
        // the distinct data_import_error / data_export_error types (error kept in
        // the payload), so success rates fall out of the event names directly.
        'as-files-imported': (ev: Event) => {
          const e = ev as CustomEvent<{ fileInfo?: FileInfoArr; fileInfoArr?: FileInfoArr; info?: string; error?: string }>;
          const fileInfo = e.detail.fileInfo ?? e.detail.fileInfoArr ?? [];
          this.sendLog(e.detail.error ? 'data_import_error' : 'data_import', 'local',
            { fileInfo, ...(e.detail.info && { info: e.detail.info }), ...(e.detail.error && { error: e.detail.error }) });
        },
        'as-data-import': (ev: Event) => { const { subcategory = 'local', ...rest } = (ev as CustomEvent).detail ?? {}; this.sendLog(rest.error ? 'data_import_error' : 'data_import', subcategory, rest); },
        'as-data-export': (ev: Event) => { const { subcategory = 'local', ...rest } = (ev as CustomEvent).detail ?? {}; this.sendLog(rest.error ? 'data_export_error' : 'data_export', subcategory, rest); },
        'as-log-to-server':    (ev: Event) => { const { evName, data } = (ev as CustomEvent).detail; this.sendLog(evName, data); },
        'as-get-app': (event: Event) => {
          (event as CustomEvent).detail.requester.dispatchEvent(new CustomEvent('as-app', { detail: { sender: this } }));
        },
        'error': (event: Event) => {
          this.logError((event as CustomEvent).detail.origin ?? 'component', (event as CustomEvent).detail.error, 'critical', 'Got error');
          this.showErrorModal((event as CustomEvent).detail);
        },
        'get-available-downloads': (event: Event) => {
          (event as CustomEvent).detail.resolve({ app: appId, downloads: 10 });
        },
      } as Record<string, EventListener>;

      this._meshWindowListeners = {
        'as-download-mesh': (event: Event) => {
          const { finalized = false, type = 'stl', fileHandle } = (event as CustomEvent).detail || {};
          MeshHandlers.downloadMesh(this as any, finalized, type, fileHandle).catch((e: unknown) => this.logError('as-download-mesh', e));
        },
      } as Record<string, EventListener>;

      registerListeners(this, this._meshSelfListeners);
      registerListeners(window, this._meshWindowListeners);
      if (this.modalOverlay) this.modalOverlay.addEventListener('click', this._overlayClickHandler);
    }

    disconnectedCallback() {
      unregisterListeners(this, this._meshSelfListeners);
      unregisterListeners(window, this._meshWindowListeners);
      if (this.modalOverlay) this.modalOverlay.removeEventListener('click', this._overlayClickHandler);
      super.disconnectedCallback?.();
    }
  };
